/** 画面側のガード。UX のためのもので、権限の真実は API 側にある。 */
import { Alert, Flex, Spin } from "antd";
import type { ReactNode } from "react";

import { hasFeature } from "../lib/features";
import { isAnyAdmin } from "../lib/roles";
import { useMe } from "../lib/useApi";
import { Forbidden } from "./Forbidden";

function Loading() {
  return (
    <Flex align="center" justify="center" style={{ padding: 64 }}>
      <Spin />
    </Flex>
  );
}

function useGate() {
  const me = useMe();
  if (me.isLoading) return { node: <Loading />, me: undefined };
  if (me.isError)
    return {
      node: (
        <Alert
          type="error"
          showIcon
          message="自分の情報を取得できなかった"
          description={me.error instanceof Error ? me.error.message : String(me.error)}
          style={{ margin: 24 }}
        />
      ),
      me: undefined,
    };
  return { node: null, me: me.data };
}

/**
 * そのアプリのロールが無ければ 403(直リンクで来た場合も同じ)。
 *
 * `/api/core/apps` は**契約で全機能が落ちているアプリも返さない**ので、
 * ここはロール不足と契約外の両方を受け止める(理由の両方を書く)。
 */
export function RequireApp({ appKey, children }: { appKey: string; children: ReactNode }) {
  const { node, me } = useGate();
  if (node) return node;
  if (!me!.apps.includes(appKey))
    return (
      <Forbidden
        detail={`このアプリ(${appKey})を使うロールが無いか、いまのプランに含まれていない。`}
      />
    );
  return <>{children}</>;
}

/**
 * ナビ項目の機能(`NavItem.feature`)が契約に無ければ 403(直リンクで来た場合も同じ)。
 *
 * ナビは `visibleGroups` で隠すが、ルートは `RequireApp`(アプリのロール)しか見ない。
 * 同じアプリの別の機能だけ契約している人が直リンクで開くと、ページが契約外の API を
 * 叩いて取得エラーを出す(コードレビュー低 18)。真実は API 側(`require_feature`)。
 */
export function RequireFeature({ feature, children }: { feature: string | string[]; children: ReactNode }) {
  const { node, me } = useGate();
  if (node) return node;
  if (!hasFeature(me, feature))
    return <Forbidden detail="この画面の機能は、いまのプランに含まれていない。" />;
  return <>{children}</>;
}

/** suite:admin か、いずれかのアプリの admin。 */
export function RequireAnyAdmin({ children }: { children: ReactNode }) {
  const { node, me } = useGate();
  if (node) return node;
  if (!isAnyAdmin(me)) return <Forbidden detail="監査ログの閲覧には admin 権限が必要。" />;
  return <>{children}</>;
}

/** suite:admin だけ。 */
export function RequireSuiteAdmin({ children }: { children: ReactNode }) {
  const { node, me } = useGate();
  if (node) return node;
  if (!me!.is_suite_admin) return <Forbidden detail="設定の変更は suite:admin だけ。" />;
  return <>{children}</>;
}
